import { API_URL } from "env";
import { mixLogOut } from "../services/mixin";

/**
 * API thống kê dashboard.
 *  - getDashboardStatistics: GET /api/v1/be/dashboard/statistics?year=YYYY
 *    Trả về doanh thu, tổng khách hàng mới và tổng lịch hẹn hoàn thành
 *    (customer_tag_id = 19) của 12 tháng trong năm được chọn.
 *  - getPagePerformance: GET /api/v1/be/dashboard/page-performance?start_date&end_date
 *    Thống kê hiệu suất trực page trong khoảng thời gian: tổng khách chat,
 *    khách hàng mới, lịch hẹn; khách theo thẻ S0-S4; kết quả cuộc gọi;
 *    khách theo kênh (fanpage); doanh thu theo nền tảng; thống kê theo nhân sự.
 */

export interface DashboardMonthStat {
    month: number;
    revenue: number;
    total_new_customers: number;
    total_completed_appointments: number;
}

export interface DashboardStatisticsData {
    year: number;
    chart: DashboardMonthStat[];
}

export interface PagePerformanceTagStat {
    id: number;
    name: string;
    color: string | null;
    description: string | null;
    count: number;
}

export interface PagePerformanceCallResult {
    id: number | null;
    name: string;
    count: number;
    ratio: number;
}

export interface PagePerformanceChannelStat {
    name: string;
    count: number;
}

export interface PagePerformancePlatformRevenue {
    id: number;
    name: string;
    revenue: number;
}

export interface PagePerformanceStaffStat {
    staff_id: number;
    staff_name: string;
    staff_avatar: string | null;
    channel_customer_count: number;
    customer_count: number;
}

export interface PagePerformanceData {
    time_range: { start_date: string; end_date: string };
    summary: {
        total_chat_customers: number;
        total_new_customers: number;
        total_appointments: number;
    };
    customer_by_tags: PagePerformanceTagStat[];
    call_result_summary: PagePerformanceCallResult[];
    customer_by_channels: PagePerformanceChannelStat[];
    revenue_by_platforms: PagePerformancePlatformRevenue[];
    staff_customer_stats: PagePerformanceStaffStat[];
}

export async function getPagePerformance(
    startDate: string,
    endDate: string
): Promise<{ status: boolean; data?: PagePerformanceData; msg?: string }> {
    try {
        const q = new URLSearchParams();
        q.append("start_date", startDate);
        q.append("end_date", endDate);
        const res = await fetch(`${API_URL}/api/v1/be/dashboard/page-performance?${q.toString()}`, {
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
            },
        });
        if (!res.ok) {
            if (res.status === 401) { mixLogOut(); }
            throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();
        return { status: !!json.status, data: json.data, msg: json.msg };
    } catch (e: any) {
        return { status: false, msg: e?.message };
    }
}

/**
 * Hiệu suất Telesale (New) — GET /api/v1/be/dashboard/telesale-performance-new
 * Query: start_date, end_date (bắt buộc), branch_id (tùy chọn, 'all' = tất cả).
 */

export interface TelesaleRanking {
    staff_id: number;
    name: string;
    avatar: string | null;
    booked: number;
    showup: number;
    closed: number;
    showup_rate: number;
    close_rate: number;
    rank: number;
}

export interface TelesaleTagStat {
    tag: string;
    code: string;
    total: number;
    desc?: string;
}

export interface TelesaleCallResult {
    name: string;
    total: number;
    ratio: number;
}

export interface TelesaleServiceRanking {
    name: string;
    code: string;
    booked: number;
    closed: number;
    ratio: number;
}

export interface TelesalePerformanceData {
    time_range: { start_date: string; end_date: string };
    branches: { id: number; name: string }[];
    summary: {
        total_booked: number;
        total_showup: number;
        showup_rate: number;
        total_closed: number;
        close_rate: number;
    };
    rankings: TelesaleRanking[];
    tag_summary: TelesaleTagStat[];
    call_result_summary: TelesaleCallResult[];
    service_rankings: TelesaleServiceRanking[];
    revenue: {
        current_period: number;
        prev_period: number;
        difference: number;
        difference_percent: number;
    };
}

export async function getTelesalePerformance(
    startDate: string,
    endDate: string,
    branchId?: number | string
): Promise<{ status: boolean; data?: TelesalePerformanceData; msg?: string }> {
    try {
        const q = new URLSearchParams();
        q.append("start_date", startDate);
        q.append("end_date", endDate);
        if (branchId !== undefined && branchId !== null && branchId !== "") q.append("branch_id", String(branchId));
        const res = await fetch(`${API_URL}/api/v1/be/dashboard/telesale-performance-new?${q.toString()}`, {
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
            },
        });
        if (!res.ok) {
            if (res.status === 401) { mixLogOut(); }
            throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();
        return { status: !!json.status, data: json.data, msg: json.msg };
    } catch (e: any) {
        return { status: false, msg: e?.message };
    }
}

/**
 * Hiệu suất Sale Cơ Sở — GET /api/v1/be/dashboard/sale-branch-performance
 * Query: start_date, end_date (bắt buộc), branch_id (tùy chọn, 'all' = tất cả).
 */

export interface SaleBranchRanking {
    staff_id: number;
    name: string;
    avatar: string | null;
    showup: number;
    closed: number;
    rate: number;
    revenue: number;
    aov: number;
    rank: number;
}

export interface SaleBranchStatusStat {
    name: string;
    total: number;
    tag?: string;
}

export interface SaleBranchServiceStat {
    code: string;
    name: string;
    total: number;
    revenue: number;
}

/** Lượng khách đến cơ sở theo giờ trong ngày hiện tại (hôm nay vs hôm qua) */
export interface SaleBranchTrafficStatistics {
    hours: string[];
    today: number[];
    yesterday: number[];
    summary: {
        total_today: number;
        total_yesterday: number;
        /** Chênh lệch hôm nay so với hôm qua (dương = tăng, âm = giảm) */
        diff: number;
    };
}

export interface SaleBranchPerformanceData {
    time_range: { start_date: string; end_date: string };
    branches: { id: number; name: string }[];
    summary: {
        total_showup: number;
        total_closed: number;
        close_rate: number;
        total_revenue: number;
        avg_order_value: number;
    };
    rankings: SaleBranchRanking[];
    status_summary: SaleBranchStatusStat[];
    service_summary: SaleBranchServiceStat[];
    traffic_statistics?: SaleBranchTrafficStatistics;
}

export async function getSaleBranchPerformance(
    startDate: string,
    endDate: string,
    branchId?: number | string
): Promise<{ status: boolean; data?: SaleBranchPerformanceData; msg?: string }> {
    try {
        const q = new URLSearchParams();
        q.append("start_date", startDate);
        q.append("end_date", endDate);
        if (branchId !== undefined && branchId !== null && branchId !== "") q.append("branch_id", String(branchId));
        const res = await fetch(`${API_URL}/api/v1/be/dashboard/sale-branch-performance?${q.toString()}`, {
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
            },
        });
        if (!res.ok) {
            if (res.status === 401) { mixLogOut(); }
            throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();
        return { status: !!json.status, data: json.data, msg: json.msg };
    } catch (e: any) {
        return { status: false, msg: e?.message };
    }
}

/**
 * Biểu đồ 12 tháng các Cơ sở — GET /api/v1/be/dashboard/sale-branch-statistics?year=YYYY
 * Ngoài các mảng cố định, response còn có các khóa động `success_branch_{id}`
 * (số ca thành công S6+ của từng chi nhánh theo tháng) — đối chiếu id với `branches`.
 */
export interface SaleBranchStatisticsData {
    year: number;
    months: string[];
    branches: { id: number; name: string }[];
    /** Số đơn > 10 triệu (nhóm S8+) theo từng tháng */
    vip_orders_s8: number[];
    /** Tổng doanh thu nhóm S8+ theo từng tháng */
    revenue_s8: number[];
    /** Đơn trung bình (AOV) nhóm S8+ theo từng tháng */
    aov_s8: number[];
    [key: string]: any;
}

export async function getSaleBranchStatistics(
    year?: number
): Promise<{ status: boolean; data?: SaleBranchStatisticsData; msg?: string }> {
    try {
        const q = new URLSearchParams();
        if (year) q.append("year", String(year));
        const res = await fetch(`${API_URL}/api/v1/be/dashboard/sale-branch-statistics?${q.toString()}`, {
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
            },
        });
        if (!res.ok) {
            if (res.status === 401) { mixLogOut(); }
            throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();
        return { status: !!json.status, data: json.data, msg: json.msg };
    } catch (e: any) {
        return { status: false, msg: e?.message };
    }
}

/**
 * Biểu đồ Telesale theo tháng (New) — GET /api/v1/be/dashboard/telesale-statistics-new?year=YYYY
 * chart là object dạng { "1": {new, success, canceled}, ..., "12": {...} }
 */

export interface TelesaleMonthStat {
    new: number;
    success: number;
    canceled: number;
}

export async function getTelesaleStatistics(
    year?: number
): Promise<{ status: boolean; year?: number; chart: Record<string, TelesaleMonthStat>; msg?: string }> {
    try {
        const q = new URLSearchParams();
        if (year) q.append("year", String(year));
        const res = await fetch(`${API_URL}/api/v1/be/dashboard/telesale-statistics-new?${q.toString()}`, {
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
            },
        });
        if (!res.ok) {
            if (res.status === 401) { mixLogOut(); }
            throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();
        return { status: !!json.status, year: json.data?.year, chart: json.data?.chart || {}, msg: json.msg };
    } catch (e: any) {
        return { status: false, chart: {}, msg: e?.message };
    }
}

export async function getDashboardStatistics(
    year?: number
): Promise<{ status: boolean; data?: DashboardStatisticsData; msg?: string }> {
    try {
        const q = new URLSearchParams();
        if (year) q.append("year", String(year));
        const res = await fetch(`${API_URL}/api/v1/be/dashboard/statistics?${q.toString()}`, {
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
            },
        });
        if (!res.ok) {
            if (res.status === 401) { mixLogOut(); }
            throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();
        return { status: !!json.status, data: json.data, msg: json.msg };
    } catch (e: any) {
        return { status: false, msg: e?.message };
    }
}

/**
 * Báo cáo Chiến dịch Marketing & SLA phản hồi tin nhắn
 * — GET /api/v1/be/dashboard/marketing-campaign-report
 * Gom KPI tổng quan, tỷ trọng kênh, danh sách chiến dịch Facebook Ads
 * và bảng xếp hạng thời gian phản hồi theo nhân viên trực page.
 * Không truyền ngày → BE mặc định từ ngày 01 đầu tháng đến hôm nay.
 */
export interface MarketingReportPeriod {
    start_date: string;
    end_date: string;
    display: string;
    working_hours: string;
    sla_minutes: number;
}

export interface MarketingReportSummary {
    total_campaigns: number;
    total_campaigns_sub?: string;
    total_budget: number;
    total_budget_formatted: string;
    total_spent: number;
    total_spent_formatted: string;
    spent_percent_of_budget?: number;
    spent_percent_formatted?: string;
    total_messages: number;
    total_messages_sub?: string;
    ad_messages: number;
    ad_messages_percent?: number;
    ad_messages_sub?: string;
    organic_messages: number;
    organic_messages_percent?: number;
    organic_messages_sub?: string;
    total_comments: number;
    total_comments_sub?: string;
    avg_cost_per_message: number;
    avg_cost_per_message_formatted: string;
    comment_to_message_rate: number;
    total_revenue: number;
    total_revenue_formatted: string;
    avg_response_minutes: number;
    overall_sla_rate: number;
    staff_achieved_sla: string;
    active_campaigns_count: number;
    active_campaigns_ratio: string;
    active_campaigns_note: string;
}

export interface MarketingReportChannelCount {
    name: string;
    count: number;
    percent: number;
}

export interface MarketingReportChannelSpend {
    name: string;
    campaign_count: number;
    campaign_label: string;
    spent: number;
    spent_formatted: string;
    avg_cost_per_message: number;
    avg_cost_per_message_formatted: string;
    is_integrated: boolean;
    note?: string;
}

export interface MarketingReportHighlight {
    title: string;
    campaign_id?: string;
    campaign_name?: string;
    display: string;
}

export interface MarketingReportCampaign {
    id: number;
    fb_campaign_id: string;
    name: string;
    channel: string;
    status: string;
    effective_status: string;
    budget: number;
    budget_formatted: string;
    spent: number;
    spent_formatted: string;
    messages: number;
    leads: number;
    cost_per_message: number;
    cost_per_message_formatted: string;
    revenue: number;
    revenue_formatted: string;
    ad_sets_count: number;
    ads_count: number;
    badges: string[];
    badge_text: string;
    is_most_messages: boolean;
    is_lowest_cost: boolean;
    is_highest_revenue: boolean;
}

export interface MarketingReportStaff {
    user_id: number;
    staff_id: number;
    name: string;
    avatar: string | null;
    initials: string;
    processed_messages: number;
    avg_response_minutes: number;
    time_display: string;
    sla_rate_percent: number;
    status: 'pass' | 'fail';
    status_label: string;
}

export interface MarketingCampaignReportData {
    period: MarketingReportPeriod;
    summary: MarketingReportSummary;
    channel_breakdown: {
        messages_proportion: {
            total_messages: number;
            facebook: MarketingReportChannelCount;
            tiktok: MarketingReportChannelCount;
        };
        spend_and_performance: {
            facebook: MarketingReportChannelSpend;
            tiktok: MarketingReportChannelSpend;
        };
    };
    campaign_highlights: {
        most_messages?: MarketingReportHighlight | null;
        lowest_cost?: MarketingReportHighlight | null;
        highest_revenue?: MarketingReportHighlight | null;
        avg_cost_per_message?: MarketingReportHighlight | null;
    };
    campaigns: MarketingReportCampaign[];
    staff_response_ranking: MarketingReportStaff[];
}

export interface MarketingCampaignReportParams {
    start_date?: string;
    end_date?: string;
    work_start?: string;
    work_end?: string;
    sla_minutes?: number;
}

export async function getMarketingCampaignReport(
    params: MarketingCampaignReportParams = {}
): Promise<{ status: boolean; data?: MarketingCampaignReportData; msg?: string }> {
    try {
        const q = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== '') q.append(k, String(v));
        });
        const res = await fetch(`${API_URL}/api/v1/be/dashboard/marketing-campaign-report?${q.toString()}`, {
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
            },
        });
        if (!res.ok) {
            if (res.status === 401) { mixLogOut(); }
            throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();
        return { status: !!json.status, data: json.data, msg: json.msg };
    } catch (e: any) {
        return { status: false, msg: e?.message };
    }
}
